/* eslint-disable @typescript-eslint/no-explicit-any */
import { useParams, useNavigate } from 'react-router-dom';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { 
  useDerivativeSecurityDetail, 
  useUpdateDerivativeSecurity, 
  useDeleteDerivativeSecurity,
} from '@derivativeSecurityHooks';
import { useState } from 'react';
import { toast } from 'sonner';
import { 
  PageHeaderSkeleton, 
  AccountCardSkeleton 
} from '@/components/LoadingSkeletons';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription 
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  ArrowLeft,
  Edit, 
  Trash2,
  ArrowUpCircle,
  ArrowDownCircle,
  Calendar,
  TrendingUp,
  TrendingDown,
  AlertTriangle
} from 'lucide-react';
import { DerivativeForm } from '@/components/derivative-form/DerivativeForm';
import { format } from 'date-fns';
import { SecuritiesErrorState } from '@/components/securities-error-state/SecuritiesErrorState';
import {DerivativeSecurityWithProfitLoss} from "@/services/investments/securities/derivatives/types.ts";

/**
 * DerivativeDetailPage component displays detailed information for a single derivative security.
 * Accessible at /investments/securities/derivatives/:id
 */
export default function DerivativeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // State for current underlying price (for profit/loss calculation)
  const [currentUnderlyingPrice, setCurrentUnderlyingPrice] = useState<number | undefined>(undefined);

  // Dialog state
  const [editFormOpen, setEditFormOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Fetch derivative security detail
  const { 
    data: derivativeDetail, 
    isLoading, 
    error,
    refetch,
  } = useDerivativeSecurityDetail(id!, currentUnderlyingPrice);

  // Mutation hooks
  const updateDerivative = useUpdateDerivativeSecurity();
  const deleteDerivative = useDeleteDerivativeSecurity();

  // Handler functions
  const handleBack = () => {
    navigate('/investments/securities/derivatives');
  };

  const handleEdit = () => {
    setEditFormOpen(true);
  };

  const handleDelete = () => {
    setDeleteDialogOpen(true);
  };

  const handleSaveDerivative = async (data: any) => {
    try {
      await updateDerivative.mutateAsync({
        id: id!,
        ...data,
      });
      toast.success('Derivative security updated successfully');
      setEditFormOpen(false);
    } catch (error) {
      console.error('Error updating derivative:', error);
      toast.error('Failed to update derivative security');
    }
  };

  const handleCancelEdit = () => {
    setEditFormOpen(false);
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteDerivative.mutateAsync(id!);
      toast.success('Derivative security deleted successfully');
      navigate('/investments/securities/derivatives');
    } catch (error) {
      console.error('Error deleting derivative:', error);
      toast.error('Failed to delete derivative security');
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
  };

  const formatCurrency = (amount: number, currency: string) => {
    const formatted = amount.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `${currency} ${formatted}`;
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch {
      return dateString;
    }
  };

  // Show loading skeletons while data is loading
  if (isLoading) {
    return (
      <ErrorBoundary
        onReset={() => {
          window.location.reload();
        }}
      >
        <div className="space-y-6" aria-busy="true" aria-live="polite">
          <PageHeaderSkeleton />
          <div className="space-y-4">
            <AccountCardSkeleton />
            <AccountCardSkeleton />
          </div>
        </div>
      </ErrorBoundary>
    );
  }

  // Show error state
  if (error || !derivativeDetail) {
    return (
      <ErrorBoundary onReset={() => { window.location.reload(); }}>
        <div className="space-y-6">
          <SecuritiesErrorState
            message="Failed to load derivative security details. Please try again."
            onRetry={() => refetch()}
            action={
              <Button onClick={handleBack} variant="outline">
                <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
                Back to Derivatives
              </Button>
            }
          />
        </div>
      </ErrorBoundary>
    );
  }

  const derivative = derivativeDetail as DerivativeSecurityWithProfitLoss

  const isOption = derivative.derivative_type === 'option';
  const isFuture = derivative.derivative_type === 'future';
  const hasProfit = derivative.profitLoss !== undefined && derivative.profitLoss >= 0;

  return (
    <ErrorBoundary
      onReset={() => {
        window.location.reload();
      }}
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={handleBack}
              variant="ghost"
              size="icon"
              aria-label="Back to derivatives"
              className="min-h-[44px] min-w-[44px]"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            </Button>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
                  {derivative.underlying_asset}
                </h1>
                <Badge variant={isOption ? 'default' : 'secondary'}>
                  {derivative.derivative_type.toUpperCase()}
                </Badge>
                {isOption && derivative.option_type && (
                  <Badge variant="outline">
                    {derivative.option_type.toUpperCase()}
                  </Badge>
                )}
                {derivative.isNearExpiry && (
                  <Badge variant="destructive" className="flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                    Near Expiry
                  </Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground sm:text-base">
                {derivative.exchange}
              </p>
            </div>
          </div>
          <div className="flex gap-2" role="group" aria-label="Derivative actions">
            <Button
              onClick={handleEdit}
              variant="outline"
              aria-label="Edit derivative security"
              className="min-h-[44px]"
            >
              <Edit className="mr-2 h-4 w-4" aria-hidden="true" />
              Edit
            </Button>
            <Button
              onClick={handleDelete}
              variant="outline"
              aria-label="Delete derivative security"
              className="min-h-[44px]"
            >
              <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" />
              Delete
            </Button>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Days Until Expiration
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold flex items-center gap-2">
                {derivative.daysUntilExpiration}
                <Calendar className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Contract Size
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {derivative.contract_size.toLocaleString(undefined, { 
                  minimumFractionDigits: 2, 
                  maximumFractionDigits: 6 
                })}
              </div>
            </CardContent>
          </Card>

          {derivative.currentValue !== undefined && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Current Value
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatCurrency(derivative.currentValue, derivative.currency)}
                </div>
              </CardContent>
            </Card>
          )}

          {derivative.profitLoss !== undefined && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Profit/Loss
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className={`text-2xl font-bold flex items-center gap-2 ${hasProfit ? 'text-emerald-600' : 'text-red-600'}`}>
                  {hasProfit ? '+' : ''}{formatCurrency(derivative.profitLoss, derivative.currency)}
                  {hasProfit ? (
                    <TrendingUp className="h-5 w-5" aria-hidden="true" />
                  ) : (
                    <TrendingDown className="h-5 w-5" aria-hidden="true" />
                  )}
                </div>
                {derivative.profitLossPercentage !== undefined && (
                  <p className={`text-sm ${hasProfit ? 'text-emerald-600' : 'text-red-600'}`}>
                    {hasProfit ? '+' : ''}{derivative.profitLossPercentage.toFixed(2)}%
                  </p>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Contract Specifications */}
        <Card>
          <CardHeader>
            <CardTitle>Contract Specifications</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Type</p>
                <p className="text-lg font-medium capitalize">{derivative.derivative_type}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Underlying Asset</p>
                <p className="text-lg font-medium">{derivative.underlying_asset}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Exchange</p>
                <p className="text-lg font-medium">{derivative.exchange}</p>
              </div>
              
              {isOption && (
                <>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Option Type</p>
                    <p className="text-lg font-medium capitalize">{derivative.option_type}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Strike Price</p>
                    <p className="text-lg font-medium">
                      {derivative.strike_price ? formatCurrency(derivative.strike_price, derivative.currency) : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Premium Paid</p>
                    <p className="text-lg font-medium">
                      {derivative.premium_paid ? formatCurrency(derivative.premium_paid, derivative.currency) : 'N/A'}
                    </p>
                  </div>
                </>
              )}

              {isFuture && (
                <>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Contract Price</p>
                    <p className="text-lg font-medium">
                      {derivative.contract_price ? formatCurrency(derivative.contract_price, derivative.currency) : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Margin Requirement</p>
                    <p className="text-lg font-medium">
                      {derivative.margin_requirement ? formatCurrency(derivative.margin_requirement, derivative.currency) : 'N/A'}
                    </p>
                  </div>
                </>
              )}

              <div>
                <p className="text-sm text-muted-foreground mb-1">Expiration Date</p>
                <p className="text-lg font-medium">
                  {formatDate(derivative.expiration_date)}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Currency</p>
                <p className="text-lg font-medium">{derivative.currency}</p>
              </div>
              {derivative.notes && (
                <div className="md:col-span-2 lg:col-span-3">
                  <p className="text-sm text-muted-foreground mb-1">Notes</p>
                  <p className="text-lg font-medium">{derivative.notes}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Transaction History */}
        <Card>
          <CardHeader>
            <CardTitle>Transaction History</CardTitle>
          </CardHeader>
          <CardContent>
            {derivative.transactions && derivative.transactions.length > 0 ? (
              <div className="space-y-3" role="list" aria-label="Transaction list">
                {derivative.transactions.map((transaction: any) => {
                  const isDeposit = transaction.transaction_type === 'deposit';
                  const Icon = isDeposit ? ArrowDownCircle : ArrowUpCircle;
                  const iconColor = isDeposit ? 'text-green-600' : 'text-blue-600';

                  return (
                    <div
                      key={transaction.id}
                      className="flex items-center justify-between p-4 border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors"
                      role="article"
                      onClick={() => navigate('/transactions/securities/' + transaction.id)}
                    >
                      <div className="flex items-center gap-3">
                        <Icon className={`h-8 w-8 ${iconColor}`} aria-hidden="true" />
                        <div>
                          <p className="font-medium capitalize">{transaction.transaction_type}</p>
                          <p className="text-sm text-muted-foreground">
                            {formatDate(transaction.transaction_date)}
                          </p>
                          {transaction.notes && (
                            <p className="text-sm text-muted-foreground">{transaction.notes}</p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">
                          {formatCurrency(transaction.amount, transaction.currency)}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <p>No transactions recorded yet</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Edit Dialog */}
        <Dialog open={editFormOpen} onOpenChange={setEditFormOpen}>
          <DialogContent 
            className="max-h-[90vh] overflow-y-auto sm:max-w-[600px] w-[95vw] sm:w-full"
            aria-describedby="edit-dialog-description"
          >
            <DialogHeader>
              <DialogTitle>Edit Derivative Security</DialogTitle>
              <DialogDescription id="edit-dialog-description">
                Update the details of your derivative position.
              </DialogDescription>
            </DialogHeader>
            <DerivativeForm
              derivative={derivative}
              onSave={handleSaveDerivative}
              onCancel={handleCancelEdit}
            />
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent 
            className="w-[95vw] sm:w-full sm:max-w-[500px]"
            aria-describedby="delete-dialog-description"
          >
            <AlertDialogHeader>
              <AlertDialogTitle>Are you sure?</AlertDialogTitle>
              <AlertDialogDescription id="delete-dialog-description">
                This action cannot be undone. This will permanently delete the derivative security
                and all associated transaction history.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="flex-col gap-2 sm:flex-row">
              <AlertDialogCancel 
                onClick={handleCancelDelete} 
                className="min-h-[44px] w-full sm:w-auto"
                aria-label="Cancel deletion"
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleConfirmDelete}
                className="min-h-[44px] w-full sm:w-auto bg-destructive text-destructive-foreground hover:bg-destructive/90"
                aria-label="Confirm deletion of derivative security"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </ErrorBoundary>
  );
}
